/**
 * RecentlyVisited — AWS-console-style "recently visited" panel for the top of
 * the App/Review page.
 *
 * The top band is split into two halves:
 *   • LEFT  — the user's most-recently-opened networks & rooms, click-to-go.
 *   • RIGHT — reserved for future use (labeled placeholder).
 *
 * Recency source is the SAME per-device signal the sidebar uses: the
 * ``recentlyAccessed`` localStorage map (stamped by WorkbenchShell on every
 * network/room open) blended with server timestamps ("access wins"), ordered by
 * ``computeRecentSearchList``. Networks + rooms come from the shell via
 * ``useOutletContext`` — no extra fetch. Per-device, private, best-effort: if
 * nothing has been opened yet the panel simply shows an empty hint.
 */
import React, { useMemo } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { computeRecentSearchList } from '@stepflow/ui-shared/utils/recentSearchList.js';
import { getAccessMap, accessedIso } from '@stepflow/ui-shared/utils/recentlyAccessed.js';
import NetworkAvatar, { networkVisual } from '@stepflow/ui-shared/components/dashboard/NetworkAvatar.jsx';
import RoomAvatar, { roomVisual } from '@stepflow/ui-shared/components/dashboard/RoomAvatar.jsx';

const RECENT_LIMIT = 6;

// Later of two ISO timestamps ('' when neither) — mirrors Sidebar's blend so a
// searched-and-opened item floats up the same way here.
function laterIso(a, b) {
  const ta = a ? Date.parse(a) : 0;
  const tb = b ? Date.parse(b) : 0;
  return (Number.isFinite(tb) ? tb : 0) > (Number.isFinite(ta) ? ta : 0) ? b : a;
}

export default function RecentlyVisited() {
  const navigate = useNavigate();
  // The shell exposes networks + rooms via the Outlet context (see
  // WorkbenchShell.outletContext). AppBrowsePage renders inside that Outlet.
  const ctx = useOutletContext() || {};
  const ctxNetworks = ctx.networks;
  const ctxRooms = ctx.agenticRooms;

  // Read the access map ONCE per render so the sort is stable.
  const accessMap = useMemo(() => getAccessMap(), []);

  const items = useMemo(() => {
    const networks = Array.isArray(ctxNetworks) ? ctxNetworks : [];
    const agenticRooms = Array.isArray(ctxRooms) ? ctxRooms : [];
    // networkId → display name, so a room reads "Network name (room name)" — the
    // network name gives context; the room's poolId is its own pet name. Falls
    // back to the id when the network isn't in the loaded list.
    const netNameById = {};
    for (const n of networks) {
      if (n && n.networkId) netNameById[n.networkId] = n.name || n.networkId;
    }
    const nets = computeRecentSearchList(networks, {
      query: '',
      limit: RECENT_LIMIT,
      getActivity: (n) => laterIso(n.updatedAt || n.createdAt, accessedIso(accessMap, n.networkId)),
    }).visible.map((n) => ({
      key: `net:${n.networkId}`,
      kind: 'network',
      networkId: n.networkId,
      hue: networkVisual(n.networkId).hue,   // identity border, same as the tile
      label: n.name || n.networkId,
      // Only show items the user has actually opened on this device — the point
      // of "recently VISITED" (server-recency is what the sidebar/list is for).
      accessedAt: accessedIso(accessMap, n.networkId),
      onGo: () => navigate(`/network/${encodeURIComponent(n.networkId)}`),
    }));
    const rooms = computeRecentSearchList(agenticRooms, {
      query: '',
      limit: RECENT_LIMIT,
      getActivity: (r) => laterIso(r.lastActivityAt || r.joinedAt, accessedIso(accessMap, `${r.networkId}:${r.poolId}`)),
    }).visible.map((r) => ({
      key: `room:${r.networkId}:${r.poolId}`,
      kind: 'room',
      networkId: r.networkId,
      poolId: r.poolId,
      hue: roomVisual(r.networkId, r.poolId).hue,   // room's hue (network family)
      // "Network name (room name)" — network name for context, poolId (the room's
      // pet name) in parens. Falls back to the networkId when the name isn't loaded.
      label: `${netNameById[r.networkId] || r.networkId} (${r.poolId})`,
      accessedAt: accessedIso(accessMap, `${r.networkId}:${r.poolId}`),
      onGo: () => navigate(`/agentic?networkId=${encodeURIComponent(r.networkId)}&poolId=${encodeURIComponent(r.poolId)}`),
    }));
    // Merge, keep only actually-visited, most-recent first, cap the band.
    return nets
      .concat(rooms)
      .filter((it) => it.accessedAt)
      .sort((a, b) => Date.parse(b.accessedAt) - Date.parse(a.accessedAt))
      .slice(0, RECENT_LIMIT);
  }, [ctxNetworks, ctxRooms, accessMap, navigate]);

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="recently-visited">
      {/* LEFT — recently visited */}
      <section className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="mb-3 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          <Clock className="h-3.5 w-3.5" aria-hidden /> Recently visited
        </div>
        {items.length === 0 ? (
          <p className="py-6 text-center text-xs text-gray-400" data-testid="recently-visited-empty">
            Networks and rooms you open will show up here.
          </p>
        ) : (
          <ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {items.map((it) => (
              <li key={it.key}>
                <button
                  type="button"
                  onClick={it.onGo}
                  data-testid={`recently-visited-item-${it.kind}`}
                  // Identity border in the item's own hue (network hue, or a room's
                  // network-family hue) — same visual language as the dashboard tile.
                  style={{ borderColor: `hsl(${it.hue} 55% 82%)` }}
                  className="flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-xs transition-colors hover:bg-blue-50/40"
                >
                  {/* Universal identity avatar — the same visual the tile /
                      header / dashboard show, so a recently-visited item is
                      recognizable at a glance (network square vs room circle). */}
                  {it.kind === 'network' ? (
                    <NetworkAvatar networkId={it.networkId} name={it.label} size={22} />
                  ) : (
                    <RoomAvatar networkId={it.networkId} poolId={it.poolId} name={it.label} size={22} />
                  )}
                  <span className="min-w-0 flex-1 truncate font-medium text-gray-800">{it.label}</span>
                  <span className="shrink-0 text-[9px] font-medium uppercase tracking-wide text-gray-400">
                    {it.kind === 'network' ? 'Network' : 'Room'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* RIGHT — reserved for future use (kept as a clearly-labeled slot). */}
      <section
        className="hidden items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50/60 p-4 lg:flex"
        data-testid="recently-visited-reserved"
        aria-hidden
      >
        <span className="text-[11px] text-gray-400">More insights coming soon</span>
      </section>
    </div>
  );
}
